import React from "react";
import {
  User,
  Calendar,
  FileText,
  Stethoscope,
  Pill,
  ClipboardList,
  CreditCard,
  Activity,
  Info
} from 'lucide-react';
import dayjs from "dayjs";


const stepperstyles = `
  .stepper-container {
    padding: 16px 24px 24px 24px;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  }

  .custom-stepper {
    display: flex;
    overflow-x: hidden; /* FIXED: Changed from auto to hidden */
    padding: 12px 8px 20px 8px; /* FIXED: Added horizontal padding for scaling */
    gap: 0;
    position: relative;
    margin: 0 -8px; /* FIXED: Negative margin to compensate for padding */
  }

  .step {
    flex: 1;
    position: relative;
    padding: 16px 12px;
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    z-index: 2;
    overflow: hidden;
    margin-left: -8px;
    transform-origin: center; /* FIXED: Ensure scaling happens from center */
  }

  .step:first-child {
    margin-left: 0;
  }

  /* FIXED: Special handling for last step to prevent overflow */
  .step.last {
    margin-right: 0;
  }

  .step.last.active {
    transform: scale(1.02); /* FIXED: Reduced scale for last step to prevent overflow */
  }

  /* Base background layer */
  .step-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
    clip-path: polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%, 15% 50%);
    z-index: -2;
  }

  .step:first-child .step-background {
    clip-path: polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%, 0% 50%);
  }

  .step.last .step-background {
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 15% 50%);
  }

  /* Enhanced liquid fill layer with vibrant gradients */
  .step-liquid {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      #f97316 0%,
      #fb923c 20%,
      #36b5d8 40%,
      #10b981 60%,
      #059669 80%,
      #047857 100%
    );
    background-size: 600% 600%;
    animation: liquidFlow 8s ease-in-out infinite;
    clip-path: polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%, 15% 50%);
    z-index: -1;
    transform: translateY(100%);
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: inset 0 0 20px rgba(255, 255, 255, 0.1);
  }

  .step:first-child .step-liquid {
    clip-path: polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%, 0% 50%);
  }

  .step.last .step-liquid {
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 15% 50%);
  }

  /* Faster liquid animation */
  @keyframes liquidFlow {
    0% {
      background-position: 0% 50%;
      filter: hue-rotate(0deg) brightness(1);
    }
    25% {
      background-position: 100% 0%;
      filter: hue-rotate(45deg) brightness(1.1);
    }
    50% {
      background-position: 100% 100%;
      filter: hue-rotate(90deg) brightness(1.2);
    }
    75% {
      background-position: 0% 100%;
      filter: hue-rotate(135deg) brightness(1.1);
    }
    100% {
      background-position: 0% 50%;
      filter: hue-rotate(180deg) brightness(1);
    }
  }

  /* Enhanced bubble effects */
  .step-liquid::before {
    content: '';
    position: absolute;
    top: -3px;
    left: 0;
    right: 0;
    height: 6px;
    background: 
      radial-gradient(ellipse 25px 10px at 20px -5px, rgba(255, 255, 255, 0.4) 0%, transparent 60%),
      radial-gradient(ellipse 20px 8px at 50px -3px, rgba(54, 181, 216, 0.3) 0%, transparent 60%),
      radial-gradient(ellipse 15px 6px at 80px -2px, rgba(249, 115, 22, 0.4) 0%, transparent 60%),
      radial-gradient(ellipse 12px 5px at 110px -1px, rgba(16, 185, 129, 0.3) 0%, transparent 60%);
    animation: enhancedBubbleMove 4s ease-in-out infinite;
  }

  @keyframes enhancedBubbleMove {
    0%, 100% {
      transform: translateX(0) scale(1);
      opacity: 0.7;
    }
    25% {
      transform: translateX(15px) scale(1.1);
      opacity: 1;
    }
    50% {
      transform: translateX(30px) scale(0.9);
      opacity: 0.8;
    }
    75% {
      transform: translateX(15px) scale(1.05);
      opacity: 1;
    }
  }

  /* Step states with enhanced colors */
  .step.active {
    color: white;
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
    transform: scale(1.05);
    z-index: 10;
  }

  .step.active .step-liquid {
    transform: translateY(0%) !important;
    box-shadow: 
      0 6px 25px rgba(54, 181, 216, 0.4),
      0 2px 15px rgba(249, 115, 22, 0.3),
      inset 0 0 25px rgba(255, 255, 255, 0.15);
  }

  .step.partial {
    color: white;
    text-shadow: 0 1px 3px rgba(0,0,0,0.4);
  }

  .step.completed {
    color: white;
    text-shadow: 0 1px 3px rgba(0,0,0,0.4);
  }

  .step.completed .step-liquid {
    transform: translateY(0%) !important;
    background: linear-gradient(135deg, #047857, #10b981, #36b5d8);
    animation: completedGlow 2s ease-in-out infinite;
    box-shadow: 0 4px 20px rgba(16, 185, 129, 0.5);
  }

  @keyframes completedGlow {
    0%, 100% {
      filter: brightness(1);
    }
    50% {
      filter: brightness(1.3);
    }
  }

  .step:hover {
    transform: scale(1.08);
  }

  /* FIXED: Reduced hover scale for last step */
  .step.last:hover {
    transform: scale(1.03);
  }

  .step:hover .step-background {
    background: linear-gradient(135deg, #cbd5e1, #94a3b8);
  }

  /* Enhanced shimmer effect */
  .step.active::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      rgba(54, 181, 216, 0.3),
      rgba(255, 255, 255, 0.4),
      transparent
    );
    animation: enhancedShimmer 2s infinite;
    z-index: 1;
  }

  @keyframes enhancedShimmer {
    0% {
      left: -100%;
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      left: 100%;
      opacity: 0;
    }
  }

  .bottom-details {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    font-size: 13px;
    margin-top: 8px;
  }

  .bottom-box {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .bottom-box h4 {
    font-size: 14px;
    margin-bottom: 4px;
    color: #374151;
    font-weight: 700;
    line-height: 1.2;
  }

  .bottom-box .value {
    font-size: 13px;
    color: #047857;
    font-weight: 600;
    margin-bottom: 4px;
    line-height: 1.3;
  }

  .bottom-box .time {
    font-size: 12px;
    color: #4b5563;
    line-height: 1.2;
  }

  .icon-rounded {
    width: 40px;
    height: 40px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    background: linear-gradient(135deg, #f8fafc, #f1f5f9);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }

  .icon-rounded.active {
    background: linear-gradient(135deg, #10b981, #059669);
    border-color: #10b981;
    color: white;
    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);
    transform: scale(1.05);
  }
`;



const PatientStepper = ({ stepData = [], activeSection, scrollProgress = {}, onStepClick }) => {



  const activeIndex = stepData.findIndex(step => step.id === activeSection);
  const current = stepData[activeIndex] || stepData[0];
  const previous = stepData[activeIndex - 1];

  const getStepState = (step, index) => {
    const progress = scrollProgress[step.id] || 0;
    const fillPercentage = Math.min(100, progress * 100);
    
    // FIXED: Immediate activation when it's the activeSection
    if (step.id === activeSection) {
      return { 
        isActive: true, 
        isPartial: false, 
        isCompleted: false, 
        fillPercentage: Math.max(25, fillPercentage),
        transform: `translateY(${100 - Math.max(25, fillPercentage)}%)`
      };
    } else if (index < activeIndex || progress >= 0.9) {
      return { 
        isActive: false, 
        isPartial: false, 
        isCompleted: true, 
        fillPercentage: 100,
        transform: 'translateY(0%)'
      };
    } else if (progress > 0.05) {
      return { 
        isActive: false, 
        isPartial: true, 
        isCompleted: false, 
        fillPercentage,
        transform: `translateY(${100 - Math.max(15, fillPercentage * 0.5)}%)`
      };
    } else {
      return { 
        isActive: false, 
        isPartial: false, 
        isCompleted: false, 
        fillPercentage: 0,
        transform: 'translateY(100%)'
      };
    }
  };

  return (
    <>
      <style>{stepperstyles}</style>
      <div className="stepper-container">
        {/* Stepper */}
        <div className="custom-stepper">
          {stepData.map((step, index) => {
            const { isActive, isPartial, isCompleted, transform } = getStepState(step, index);
            
            return (
              <div
                key={step.id}
                onClick={() => onStepClick(step.id)}
                className={`step ${
                  isActive ? "active" : 
                  isPartial ? "partial" : 
                  isCompleted ? "completed" : ""
                } ${index === stepData.length - 1 ? "last" : ""}`}
                style={{ 
                  color: (isActive || isPartial || isCompleted) ? 'white' : '#4b5563',
                  zIndex: isActive ? 10 : index + 1
                }}
              >
                <div className="step-background"></div>
                <div 
                  className="step-liquid"
                  style={{ transform }}
                ></div>
                {step.label}
              </div>
            );
          })}
        </div>

        {/* Bottom details */}
        <div className="bottom-details">
          {previous && (
            <div className="bottom-box">
              <div className="icon-rounded">{previous.icon}</div>
              <div>
                <h4>{previous.detailTitle}</h4>
                <div className="value">{previous.detailValue}</div>
                <div className="time">{previous.time}</div>
              </div>
            </div>
          )}
          {current && (
            <div className="bottom-box" style={{ textAlign: "right" }}>
              <div>
                <h4>{current.detailTitle}</h4>
                <div className="value">{current.detailValue}</div>
                <div className="time">{current.time}</div>
              </div>
              <div className="icon-rounded active">{current.icon}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PatientStepper;
